import { ApiError, type ApiClient } from './client';

/**
 * The dashboard's figures, as `GET /v1/reports/summary` returns them.
 *
 * Read off `veri-plus-backend`: `routes/v1/report.routes.ts` validates
 * `summaryQuery` (`from`, `to`, both optional dates) and
 * `scanAggregation.summarize` answers the shape below with every verdict and
 * context key present, zeroed. The window is `receivedAt`, server time, and
 * `from` and `to` are both inclusive. See D9.
 */

export type ScanVerdict = 'genuine' | 'suspect' | 'unknown';
export type ScanContext = 'consumer' | 'inventory' | 'inspector';

export interface ScanSummary {
  totalScans: number;
  /** Distinct codes scanned in the window, not codes issued. */
  uniqueCodes: number;
  byVerdict: Record<ScanVerdict, number>;
  byContext: Record<ScanContext, number>;
}

/** A Monday-to-Sunday week, UTC. Null before the org's first scan, as against a zero week. */
export interface WeekBucket {
  start: string;
  total: number | null;
  suspect: number | null;
}

/** A cell at precision 1 with its town, or null where no town lies within reach. */
export interface TopPlace {
  lat: number;
  lng: number;
  total: number;
  suspect: number;
  place: { label: string; countryCode: string } | null;
}

/**
 * The same route as read for the dashboard, backend `report.service.ts:46-73`
 * at `a55d34e`. `codes` is now, not the window. `weeks` ignores the window and
 * is always the last eight, oldest first. `places.top` is up to five cells.
 */
export interface DashboardSummary extends ScanSummary {
  codes: { active: number; products: number };
  weeks: WeekBucket[];
  places: { located: number; top: TopPlace[] };
}

export interface ReportRange {
  from?: Date;
  to?: Date;
}

export function summaryPath(range: ReportRange): string {
  const query = new URLSearchParams();
  if (range.from) query.set('from', range.from.toISOString());
  if (range.to) query.set('to', range.to.toISOString());
  const qs = query.toString();
  return qs === '' ? '/v1/reports/summary' : `/v1/reports/summary?${qs}`;
}

export function getSummary(
  api: ApiClient,
  range: ReportRange,
  signal?: AbortSignal,
): Promise<ScanSummary> {
  return api.get<ScanSummary>(summaryPath(range), signal ? { signal } : {});
}

export function getDashboardSummary(
  api: ApiClient,
  range: ReportRange,
  signal?: AbortSignal,
): Promise<DashboardSummary> {
  return api.get<DashboardSummary>(summaryPath(range), signal ? { signal } : {});
}

/** `GET /v1/organization`, `organizationController.myOrganization`. Only the name is read here. */
export interface Organization {
  id: string;
  name: string;
}

export async function getOrganization(api: ApiClient, signal?: AbortSignal): Promise<Organization> {
  const body = await api.get<{ organization?: Partial<Organization> | null } | null>(
    '/v1/organization',
    signal ? { signal } : {},
  );
  const organization = body?.organization;
  if (organization && typeof organization.name === 'string') return organization as Organization;
  // A 200 that is not an organization. Thrown here, so the screen's failed arm draws it, DEFERRED 4.16.
  throw new ApiError(200, 'unrecognised_response', 'The API answered without an organization.');
}

const day = 24 * 60 * 60 * 1000;

/**
 * The three windows the four cards read. The board's subtitle says "last 30
 * days", its delta says "vs previous 30 days", and its suspect line says "this
 * week", read as the last 7 days, D9.
 */
export function dashboardWindows(now: Date): {
  current: ReportRange;
  previous: ReportRange;
  week: ReportRange;
} {
  const t = now.getTime();
  return {
    current: { from: new Date(t - 30 * day) },
    previous: { from: new Date(t - 60 * day), to: new Date(t - 30 * day) },
    week: { from: new Date(t - 7 * day) },
  };
}

/** One stat card. An empty string is a value the board draws and no route supplies. */
export interface StatCard {
  label: string;
  value: string;
  note: string;
  tone: 'genuine' | 'muted' | 'suspect';
}

const count = (n: number) => n.toLocaleString('en-US');

/**
 * The four cards `PortalDashboard` draws, from the three summaries.
 *
 * What the board does not draw is withheld rather than written, D9: the delta
 * line only when scans rose, and the genuine rate only when there was a scan.
 * Active codes is the summary's `codes`, D39.
 */
export function dashboardCards(
  current: DashboardSummary,
  previous: ScanSummary,
  week: ScanSummary,
): StatCard[] {
  const total = current.totalScans;
  const before = previous.totalScans;
  const rise = before > 0 ? Math.round(((total - before) / before) * 100) : 0;
  return [
    {
      label: 'Total scans',
      value: count(total),
      note: rise > 0 ? `↑ ${rise}% vs previous 30 days` : '',
      tone: 'genuine',
    },
    {
      label: 'Genuine rate',
      value: total > 0 ? `${((current.byVerdict.genuine / total) * 100).toFixed(1)}%` : '',
      note: 'of scans on your codes',
      tone: 'muted',
    },
    {
      label: 'Suspect scans',
      value: count(current.byVerdict.suspect),
      note: `${count(week.byVerdict.suspect)} this week, see alerts`,
      tone: 'suspect',
    },
    {
      label: 'Active codes',
      value: count(current.codes.active),
      note: `across ${count(current.codes.products)} ${current.codes.products === 1 ? 'product' : 'products'}`,
      tone: 'muted',
    },
  ];
}

/** "Grace" from "Grace O.": the board's greeting carries the first name only. */
export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? '';

/** The greeting by the browser's hour, D39: the board draws the morning string. */
export function greeting(now: Date): string {
  const hour = now.getHours();
  return hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
}

/** The board's tallest bar, W8's 112px in a 120px track. */
export const barTrack = { height: 120, tallest: 112 } as const;

export interface WeekBars {
  label: string;
  /** Null before the org's first scan: the label is drawn and no bar. */
  total: number | null;
  suspect: number | null;
  totalHeight: number;
  suspectHeight: number;
}

/**
 * The eight columns of "Scans · last 8 weeks". Each series is scaled on its
 * own so its busiest week stands at the board's tallest bar, as the board's
 * two series already stand on two scales. A count above zero is never drawn
 * as nothing. D39.
 */
export function weekBars(weeks: WeekBucket[]): WeekBars[] {
  const most = (key: 'total' | 'suspect') => Math.max(0, ...weeks.map((w) => w[key] ?? 0));
  const scale = (n: number | null, top: number) =>
    n === null || top === 0 ? 0 : Math.ceil((n / top) * barTrack.tallest);
  const topTotal = most('total');
  const topSuspect = most('suspect');
  return weeks.map((week, i) => ({
    label: `W${i + 1}`,
    total: week.total,
    suspect: week.suspect,
    totalHeight: scale(week.total, topTotal),
    suspectHeight: scale(week.suspect, topSuspect),
  }));
}

/** "Nairobi", or the cell's own coordinates where no town resolved, D39. */
export const placeLabel = (top: TopPlace) =>
  top.place?.label ?? `${top.lat.toFixed(1)}, ${top.lng.toFixed(1)}`;

/** "4,812 scans", "1 scan". */
export const scansLine = (n: number) => `${count(n)} ${n === 1 ? 'scan' : 'scans'}`;
